import { apiFetch } from "./apiFetch";
import { getMessageFromErrorBody } from "../utils/apiError";

async function readErrorMessage(resp) {
  const text = await resp.text().catch(() => "");
  return getMessageFromErrorBody(
    text,
    resp.status ? `Request failed (HTTP ${resp.status})` : "Request failed"
  );
}

/**
 * Poll Heroku background survey invite job until complete or timeout.
 * @param {{ serverUrl: string, account_id: string, user_id?: string, jobId: string, pollIntervalMs?: number, timeoutMs?: number }} args
 */
export async function pollSurveyInviteSendJob({
  serverUrl,
  account_id,
  user_id,
  jobId,
  pollIntervalMs = 2000,
  timeoutMs = 10 * 60 * 1000,
}) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    const pollResp = await apiFetch(`${serverUrl}/getSurveyInviteSendStatus`, {
      method: "POST",
      body: JSON.stringify({
        account_id,
        user_id: user_id || undefined,
        jobId,
      }),
    });
    const pollData = await pollResp.json().catch(() => ({}));
    if (pollData.status === "pending") continue;
    if (!pollResp.ok || pollData.status === "error") {
      throw new Error(
        pollData.message ||
          pollData.error ||
          (pollResp.status ? `Server returned HTTP ${pollResp.status}` : "Failed to send survey invites")
      );
    }
    return pollData;
  }
  throw new Error(
    "Survey invites are still sending on the server. Wait a minute, then refresh the invitations list."
  );
}

/**
 * Send survey invites; on Heroku follows 202 + job poll (same pattern as stock refresh).
 * @param {{ serverUrl: string, account_id: string, survey_id: string, emails: string[], user_id?: string, pending_only?: boolean }} args
 */
export async function sendSurveyInvites({
  serverUrl,
  account_id,
  survey_id,
  emails,
  user_id,
  pending_only = false,
}) {
  const resp = await apiFetch(`${serverUrl}/sendSurveyInvites`, {
    method: "POST",
    body: JSON.stringify({
      account_id,
      survey_id,
      emails,
      user_id: user_id || undefined,
      pending_only: pending_only === true,
    }),
  });

  if (resp.status === 202) {
    const data = await resp.json().catch(() => ({}));
    if (data.async && data.jobId) {
      return pollSurveyInviteSendJob({
        serverUrl,
        account_id,
        user_id,
        jobId: data.jobId,
      });
    }
    throw new Error(data.message || "Survey invite send started but no job id was returned.");
  }

  if (!resp.ok) {
    throw new Error(await readErrorMessage(resp));
  }

  return resp.json();
}
